import Base from '../../../Core/Base.js';
import ObjectHelper from '../../../Core/helper/ObjectHelper.js';

/**
 * @module Scheduler/crud/mixin/LazyLoadCrudManager
 */

/**
 * An object containing details about the requested data
 * @typedef LazyLoadCrudManagerRequestParams
 * @property {Number} startIndex The index of the first record being requested
 * @property {Number} count The number of records being requested
 * @property {Date} [startDate] The startDate of the requested date range (only if applicable)
 * @property {Date} [endDate] The endDate of the requested date range (only if applicable)
 * @property {String|Number} [parentId] The parentId for which to load children (only if Resource store is a tree store)
 * @property {String[]} [stores] The id's of the stores which the CrudManager requests data for
 */

/**
 * An object containing details about the received data used for lazy and paged loading
 * @typedef CrudManagerRequestResponse
 * @property {Object} [resources]
 * @property {Object[]} [resources.rows]
 * @property {Number} [resources.total]
 * @property {Object} [events]
 * @property {Object[]} [events.rows]
 * @property {Object} [assignments]
 * @property {Object[]} [assignments.rows]
 * @property {Object} [resourceTimeRanges]
 * @property {Object[]} [resourceTimeRanges.rows]
 * @property {Object} [timeRanges]
 * @property {Object[]} [timeRanges.rows]
 * @property {Object} [calendars]
 * @property {Object[]} [calendars.rows]
 */

const lazyDateStores = ['events', 'resourceTimeRanges', 'assignments', 'timeRanges'];

/**
 * Mixin for CrudManager that handles lazy loading.
 * @mixin
 */
export default Target => class LazyLoadCrudManager extends (Target || Base) {
    static $name = 'LazyLoadCrudManager';

    static configurable = {
        /**
         * If set to `true`, or a config object, this makes the CrudManager load records only when needed. When a record
         * or a date range that is not already loaded is requested, a load request will be made to the specified URL.
         * More more details about lazy loading, see the [guide](#Grid/guides/data/lazyloading.md)
         * @param {Object} lazyLoad Lazy load config
         * @param {Number} lazyLoad.chunkSize The number of records to be loaded before and after the requested
         * index (only affects the ResourceStore).
         * @param {DurationUnit} bufferUnit Used together with bufferAmount to calculate the start and end dates of each
         * load request. The value is added to the current visible start or end date. Defaults to the visible time span
         * length (does not affect ResourceStore).
         * @param {Number} bufferAmount See `bufferUnit`
         * @prp {Boolean|Object}
         * @category Lazy Loading
         */
        lazyLoad : false,

        /**
         * If set to `true`, this makes the CrudManager load pages of data, instead of loading the complete dataset at
         * once. The requests made to the {@link Scheduler.crud.AbstractCrudManagerMixin#config-loadUrl} will contain
         * params with info about the current dataset being requested:
         * * `page` - The resource page
         * * `pageSize` - The resource page size
         * * `startDate` - The start date of the calculated timespan
         * * `endDate` - The end date of the calculated timespan
         *
         * For more details about paging, see the [guide](#Grid/guides/data/paging.md)
         * @config {Boolean}
         * @category Lazy Loading
         */
        remotePaging : false,

        /**
         * The number of Resource records each page should contain, when using {@link #config-remotePaging}
         * @config {Number}
         * @category Lazy Loading
         */
        pageSize : 50,

        /**
         * When the CrudManager/Project is configured with {@link #config-lazyLoad} set to `true`, you can configure
         * your own data fetching logic by implementing this function. Doing this will override the built-in fetching
         * mechanism using the {@link Scheduler/data/CrudManager#config-loadUrl}.
         *
         * When implementing this, it is expected that what is returned is an object fulfilling the
         * {@link Scheduler/data/CrudManager contract} of a regular CrudManager/Project load call.
         *
         * @config {Function}
         * @param {LazyLoadCrudManagerRequestParams} params Object containing info of what data is requested
         * @returns {CrudManagerRequestResponse}
         * @async
         * @category Lazy Loading
         */
        requestData : 'defaultRequestData'
    };

    updateRemotePaging(remotePaging, old) {
        if (remotePaging) {
            this.lazyLoad = true;
        }
        else if (old) {
            this.lazyLoad = false;
        }
    }

    updatePageSize() {
        if (!this.isConfiguring) {
            this.load();
        }
    }

    updateLazyLoad(lazyLoad, old) {
        if (!lazyLoad && !old) {
            return;
        }

        const
            me                           = this,
            { internalLoad, crudStores } = me;

        // Activate/deactivate on each store
        crudStores.forEach(({ store }) => me.storeShouldLazyLoad(store) && me.setupLazyStore(store));

        // Override the default load
        if (lazyLoad) {
            if (!internalLoad) {
                me.internalLoad = me.load;
                me.load         = me.internalLazyLoad;
            }
        }
        // Or restore it
        else if (internalLoad) {
            me.load = internalLoad;
        }
    }

    changeRequestData(requestData, old) {
        if (requestData === 'defaultRequestData') {
            return this.defaultRequestData;
        }
        return requestData;
    }

    afterAddStore(stores) {
        stores.forEach(({ store }) => this.storeShouldLazyLoad(store) && this.setupLazyStore(store));
    }

    setupPagingStore(store) {
        if (this.remotePaging) {
            const { isResourceStore } = store;

            store.remotePaging = true;
            store.syncDataOnLoad = false;

            if (isResourceStore) {
                // Silent update, not to cause a load request
                store._pageSize = this.pageSize;
            }
            else {
                store.lazyLoad = true;
                store.lazyLoad.$isCrudManager = true;
            }

            // Implement resourceStore's and eventStore's requestData functions
            if (isResourceStore || store.isEventStore) {
                // This will implement the requestData function of each store that is added to the CrudManager
                store.requestData = params => this.doLazyLoad(params, isResourceStore);
            }
            // All other store's need to have their requestData removed (from AjaxStore)
            else {
                store.requestData = null;
            }
        }
        else {
            store.remotePaging = false;
        }
    }

    setupLazyStore(store) {
        if (this.remotePaging) {
            return this.setupPagingStore(store);
        }

        let { lazyLoad } = this;

        if (lazyLoad) {
            const { isResourceStore } = store;

            // Activate lazyLoad on all stores
            // If not true, it is a config object
            if (lazyLoad !== true) {
                const {
                    chunkSize,
                    bufferUnit,
                    bufferAmount
                }        = lazyLoad;
                lazyLoad = {};

                if (isResourceStore && chunkSize) {
                    // This only affects resourceStore
                    lazyLoad.chunkSize = chunkSize;
                }
                else if (!isResourceStore) {
                    // This does not affect resourceStores
                    if (bufferUnit) {
                        lazyLoad.bufferUnit = bufferUnit;
                    }
                    if (bufferAmount) {
                        lazyLoad.bufferAmount = bufferAmount;
                    }
                }
            }

            store.lazyLoad = lazyLoad;
            store.lazyLoad.$isCrudManager = true;
            store.syncDataOnLoad = false;

            // Implement resourceStore's and eventStore's requestData functions
            if (isResourceStore || store.isEventStore) {
                // This will implement the requestData function of each store that is added to the CrudManager
                store.requestData = params => this.doLazyLoad(params, isResourceStore);
            }
            // All other store's need to have their requestData removed (from AjaxStore)
            else {
                store.requestData = null;
            }
        }
        else if (store.lazyLoad?.$isCrudManager) {
            store.lazyLoad = false;
        }
    }

    internalLazyLoad(options) {
        const me = this;

        me.crudLoaded = false;
        options && (me._loadOptions ?? (me._loadOptions = [])).push(options);

        return me.resourceStore.load();
    }

    loadCrudStore(store, data, options) {
        if (this.lazyLoad && this.storeShouldLazyLoad(store)) {
            return;
        }
        return super.loadCrudStore(store, data, options);
    }

    getLazyDateStores(skipEventStore) {
        return this.crudStores.filter(store => lazyDateStores.includes(store.storeId) && (!skipEventStore || !store.store.isEventStore) && (!store.store.isAssignmentStore || !this.eventStore?.usesSingleAssignment));
    }

    storeShouldLazyLoad(store) {
        return store.isResourceStore || this.getLazyDateStores().some(s => s.store === store);
    }

    // Currently, this function will be called from the resourceStore requesting and index or from the eventStore
    // requesting a date range.
    async doLazyLoad({ isReload, ...params }, isResourceStore) {
        // No date params means we're probably not attached to a Scheduler/Gantt. Abort.
        if (!params.startDate || !params.endDate) {
            return;
        }

        const
            me             = this,
            dateStores     = me.getLazyDateStores(!isResourceStore),
            isResourceTree = isResourceStore && me.resourceStore.isTree,
            pending        = me._lazyRequestPromises || (me._lazyRequestPromises = []),
            // First load: request all stores
            // Then: request stores which has lazyLoad support, but only include resourceStore when that is the origin
            // of the request
            stores         = me.crudStores.filter(s => !me.crudLoaded || (lazyDateStores.includes(s.storeId) || (isResourceStore && s.store.isResourceStore))).map(s => s.storeId),
            promise        = Promise.all(pending).then(() => me.requestData({ ...params, stores })),
            queueParams    = { ...params, promise };

        // If resourceStore is a Tree, and we are loading resources, we queue on resource ids. Using something temporary
        // just to set the stores in a loading state as we don't know what resources we are loading at this point
        if (isResourceTree) {
            queueParams.startIndex = -1;
            queueParams.count = 1;
        }

        // All date stores (except EventStore if it's the trigger) should indicate that they are loading
        dateStores.forEach(({ store }) => {
            // If we're paged, and this is a resource-initiated load, clear all previously loaded data
            if (isResourceStore && (isReload || me.remotePaging)) {
                me.remotePaging && store.clear(true);
                store.lazyLoad.clear();
            }

            store.lazyLoad.triggerLazyLoadStart();
            store.lazyLoad.addToQueue(queueParams);
        });

        pending.push(promise);

        const
            response        = await promise,
            { rows, total } = isResourceStore ? response.resources : response.events;

        if (isResourceTree) {
            // Now we know what resources we have loaded
            const { idField } = me.resourceStore.modelClass;
            queueParams.resourceIds = rows.map(r => r[idField]);
        }

        queueParams.promise = undefined;

        // Set data to all date stores and stop the loading indicators
        dateStores.forEach(({ store : { lazyLoad }, storeId }) => {
            if (isResourceTree) {
                // Remove the "fake" queue entry from above
                delete lazyLoad.loadQueue[-1];
                // Mark the returned resources as loaded
                lazyLoad.addToQueue(queueParams);
            }

            if (response[storeId]) {
                lazyLoad.addData(response[storeId].rows, queueParams, isReload);
            }

            lazyLoad.triggerLazyLoadEnd();
        });

        // The non lazy loaded stores will get their data the normal way

        pending.splice(pending.indexOf(promise), 1);

        return {
            data  : rows,
            total : isResourceStore ? total : undefined
        };
    }

    async defaultRequestData({ stores, ...params }) {
        return await this.internalLoad(ObjectHelper.merge(this._loadOptions?.shift() ?? {}, {
            type : 'load',
            params,
            stores
        })).then(r => r.response);
    }
};
